import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Phone } from "lucide-react";
import { scrollToSection } from "@/lib/smooth";

const LINKS = [
  { label: "Home", target: "#hero", testid: "nav-link-home" },
  { label: "About", target: "#about", testid: "nav-link-about" },
  { label: "Products", target: "#products", testid: "nav-link-products" },
  { label: "Why Us", target: "#why-us", testid: "nav-link-why-us" },
  { label: "Contact", target: "#contact", testid: "nav-link-contact" },
];

const Header = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (target) => scrollToSection(target);

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ delay: 2.3, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      className="fixed inset-x-0 top-0 z-50 px-4 pt-4 sm:px-6"
    >
      <div
        className={`relative mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-x-4 gap-y-2 rounded-3xl border px-5 py-2.5 backdrop-blur-xl transition-[background-color,box-shadow,border-color] duration-500 sm:rounded-full sm:px-6 sm:py-2 md:flex-nowrap ${
          scrolled
            ? "border-gold/30 bg-parchment/85 shadow-[0_18px_50px_-20px_rgba(18,13,14,0.45)]"
            : "border-gold/15 bg-parchment/40"
        }`}
      >
        <div className="flex w-full items-center justify-between gap-3 md:w-auto md:justify-start">
          <button onClick={() => go("#hero")} data-testid="brand-home-btn" className="flex shrink-0 items-center" aria-label="Praveen Textiles — home">
            <img src="/logo-full.png" alt="Praveen Textiles" className="h-9 w-auto object-contain md:h-10 lg:h-16" />
          </button>
          <button
            data-testid="header-phone-cta"
            onClick={() => go("#contact")}
            className="flex shrink-0 items-center gap-1.5 self-center rounded-full bg-crimson px-3 py-1.5 font-body text-[11px] font-semibold text-parchment transition-[background-color] duration-300 hover:bg-crimson-bright sm:px-4 sm:py-2 sm:text-xs md:hidden"
          >
            <Phone size={14} /> Order Now
          </button>
        </div>

        <nav className="order-3 flex w-full items-center justify-center gap-5 border-t border-ink/10 pt-2 md:absolute md:left-1/2 md:w-auto md:-translate-x-1/2 md:gap-4 md:border-0 md:pt-0 lg:gap-8">
          {LINKS.map((l) => (
            <button
              key={l.label}
              data-testid={l.testid}
              onClick={() => go(l.target)}
              className="group relative shrink-0 whitespace-nowrap font-body text-[11px] font-medium tracking-wide text-ink/80 transition-colors hover:text-crimson md:text-xs lg:text-[13px]"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-gold transition-[width] duration-300 group-hover:w-full" />
            </button>
          ))}
        </nav>

        <div className="hidden shrink-0 items-center gap-3 md:flex">
          <span className="hidden items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-clay xl:flex">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-700" />
            Orders Open
          </span>
          <button
            data-testid="header-phone-cta-desktop"
            onClick={() => go("#contact")}
            className="flex items-center gap-1.5 rounded-full bg-crimson px-4 py-2 font-body text-xs font-semibold text-parchment transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-crimson-bright lg:px-5 lg:py-2.5 lg:text-[13px]"
          >
            <Phone size={14} /> Order Now
          </button>
        </div>
      </div>
    </motion.header>
  );
};

export default Header;
